"use client";

import { useEffect, useRef, useState } from "react";
import { Box, Button, HStack, IconButton, Menu, Portal, Spinner, Text, VStack } from "@chakra-ui/react";
import { useColorModeValue } from "@/components/ui/color-mode";
import { LuBell } from "react-icons/lu";

type NotificationItem = {
  _id: string;
  title?: string | null;
  message: string;
  senderType: "admin" | "bot";
  createdAt: string;
  isRead: boolean;
};

type NotificationBellProps = {
  inOverlay?: boolean;
};

const INITIAL_FETCH_LIMIT = 3;
const LOAD_MORE_LIMIT = 2;
const UNREAD_COUNT_REFRESH_LIMIT = 1;

export default function NotificationBell({ inOverlay = false }: NotificationBellProps) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loadingInitial, setLoadingInitial] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const unreadColor = useColorModeValue("teal.600", "teal.300");
  const mutedColor = useColorModeValue("gray.600", "gray.300");
  const hoverBg = useColorModeValue("blackAlpha.50", "whiteAlpha.100");

  const fetchNotifications = async ({
    offset,
    limit,
    append,
  }: {
    offset: number;
    limit: number;
    append: boolean;
  }) => {
    const response = await fetch(`/api/notifications?offset=${offset}&limit=${limit}`, {
      cache: "no-store",
    });

    const payload = (await response.json().catch(() => null)) as
      | {
          error?: string;
          notifications?: NotificationItem[];
          pagination?: { hasMore?: boolean; unreadCount?: number };
        }
      | null;

    if (!response.ok) {
      throw new Error(payload?.error ?? "Unable to load notifications.");
    }

    const incoming = Array.isArray(payload?.notifications) ? payload.notifications : [];
    setNotifications((prev) => (append ? [...prev, ...incoming] : incoming));
    setHasMore(Boolean(payload?.pagination?.hasMore));
    setUnreadCount(payload?.pagination?.unreadCount ?? 0);
  };

  const refreshUnreadCount = async () => {
    try {
      const response = await fetch(`/api/notifications?offset=0&limit=${UNREAD_COUNT_REFRESH_LIMIT}`, {
        cache: "no-store",
      });
      const payload = (await response.json().catch(() => null)) as
        | { pagination?: { unreadCount?: number } }
        | null;
      if (!response.ok) return;
      setUnreadCount(payload?.pagination?.unreadCount ?? 0);
    } catch {
      // Best-effort count refresh; no UI interruption needed.
    }
  };

  const markNotificationsAsRead = async (ids: string[]) => {
    if (ids.length === 0) return;

    await fetch("/api/notifications", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids }),
    });
  };

  const handleMarkAsRead = async (notificationId: string) => {
    const target = notifications.find((item) => item._id === notificationId);
    if (!target || target.isRead) return;

    setNotifications((prev) =>
      prev.map((item) => (item._id === notificationId ? { ...item, isRead: true } : item)),
    );
    setUnreadCount((prev) => (prev > 0 ? prev - 1 : 0));

    try {
      await markNotificationsAsRead([notificationId]);
    } catch {
      // Keep UI optimistic; next fetch will self-heal if server write failed.
    }
  };

  const formatCreatedAt = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date);
  };

  const handleOpen = async () => {
    setLoadingInitial(true);
    setError(null);
    try {
      await fetchNotifications({
        offset: 0,
        limit: INITIAL_FETCH_LIMIT,
        append: false,
      });
    } catch (fetchError) {
      setNotifications([]);
      setHasMore(false);
      setError(fetchError instanceof Error ? fetchError.message : "Unable to load notifications.");
    } finally {
      setLoadingInitial(false);
    }
  };

  const handleLoadMore = async () => {
    setLoadingMore(true);
    setError(null);
    try {
      await fetchNotifications({
        offset: notifications.length,
        limit: LOAD_MORE_LIMIT,
        append: true,
      });
    } catch (fetchError) {
      setError(fetchError instanceof Error ? fetchError.message : "Unable to load notifications.");
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    void refreshUnreadCount();
    const intervalId = window.setInterval(() => {
      void refreshUnreadCount();
    }, 30000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  const hasUnread = unreadCount > 0;
  const badgeText = unreadCount > 99 ? "99+" : String(unreadCount);
  const menuBody = (
    <Menu.Positioner>
      <Menu.Content minW={{ base: "260px", sm: "300px" }} w="calc(100vw - 24px)" maxW="360px" p={0}>
        <VStack align="stretch" gap={0}>
          <Box px={4} py={3} borderBottom="1px solid" borderColor="border.muted">
            <Text fontSize="sm" fontWeight="semibold">
              Notifications
            </Text>
          </Box>
          <Box px={4} py={4}>
            {loadingInitial ? (
              <Box py={2} display="flex" justifyContent="center">
                <Spinner size="sm" />
              </Box>
            ) : error ? (
              <Text fontSize="sm" color="red.500">
                {error}
              </Text>
            ) : notifications.length === 0 ? (
              <Text fontSize="sm" color="fg.muted">
                No notifications yet.
              </Text>
            ) : (
              <VStack align="stretch" gap={3}>
                {notifications.map((item) => (
                  <Box
                    key={item._id}
                    px={2}
                    py={2}
                    rounded="md"
                    _hover={{ bg: "blackAlpha.50", _dark: { bg: "whiteAlpha.100" } }}
                    cursor="pointer"
                    onClick={() => {
                      void handleMarkAsRead(item._id);
                    }}
                  >
                    <HStack justify="space-between" align="center" mb={1}>
                      <Text fontSize="xs" color="fg.muted">
                        {formatCreatedAt(item.createdAt)}
                      </Text>
                      {!item.isRead ? (
                        <Text fontSize="xs" fontWeight="medium" color="teal.500">
                          New
                        </Text>
                      ) : null}
                    </HStack>
                    <Text fontSize="sm" color="fg.muted" whiteSpace="normal" wordBreak="break-word">
                      {item.message}
                    </Text>
                  </Box>
                ))}
                {hasMore ? (
                  <Button
                    size="xs"
                    variant="ghost"
                    alignSelf="flex-start"
                    onClick={handleLoadMore}
                    loading={loadingMore}
                    disabled={loadingMore}
                  >
                    Load more
                  </Button>
                ) : null}
              </VStack>
            )}
          </Box>
        </VStack>
      </Menu.Content>
    </Menu.Positioner>
  );

  return (
    <Menu.Root
      positioning={
        inOverlay
          ? {
              placement: "bottom",
              strategy: "fixed",
              hideWhenDetached: true,
              getAnchorElement: () => {
                const rect = triggerRef.current?.getBoundingClientRect();
                return {
                  getBoundingClientRect: () =>
                    new DOMRect(window.innerWidth / 2, rect?.bottom ?? 0, 0, 0),
                };
              },
            }
          : { placement: "bottom-end" }
      }
      open={isOpen}
      onOpenChange={(details) => {
        setIsOpen(details.open);
        if (details.open) {
          void handleOpen();
        }
      }}
    >
      <Menu.Trigger asChild>
        <Box position="relative" display="inline-flex" ref={triggerRef}>
          <IconButton
            size="lg"
            variant="ghost"
            aria-label="Notifications"
            color={hasUnread ? unreadColor : mutedColor}
            bg="transparent"
            _hover={{
              bg: hoverBg,
            }}
            _active={{ bg: hoverBg }}
          >
            <LuBell fill={hasUnread ? "currentColor" : "none"} />
          </IconButton>
          {hasUnread ? (
            <Box
              position="absolute"
              top="-1"
              right="-1"
              minW="18px"
              h="18px"
              px="1"
              borderRadius="full"
              bg="red.500"
              color="white"
              fontSize="10px"
              fontWeight="bold"
              lineHeight="18px"
              textAlign="center"
              pointerEvents="none"
            >
              {badgeText}
            </Box>
          ) : null}
        </Box>
      </Menu.Trigger>

      {inOverlay ? menuBody : <Portal>{menuBody}</Portal>}
    </Menu.Root>
  );
}
